import { useEffect, useRef, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { euToIso, isoToEU } from "../lib/format";

/**
 * Localized date input — displays dd/mm/yyyy but exchanges ISO YYYY-MM-DD
 * with parent state. Uses a hidden native `<input type=date>` overlay so
 * users still get the OS date picker for point-and-click selection, while
 * the visible field enforces the EU format regardless of browser locale.
 *
 * Props
 *   value       string   ISO date (YYYY-MM-DD) or empty
 *   onChange    (iso)    called with ISO string on every valid edit
 *   min         string   optional ISO minimum
 *   max         string   optional ISO maximum
 *   required    bool
 *   disabled    bool
 *   testId      string   applied to the visible input as data-testid
 *   className   string   extra classes for the visible input
 *   inputSize   "sm"|"md"  default md
 */
export default function DateInput({
  value = "", onChange, min, max, required, disabled,
  testId, className = "", inputSize = "md", placeholder = "dd/mm/yyyy",
}) {
  const [text, setText] = useState(isoToEU(value));
  const nativeRef = useRef(null);

  // Sync when parent updates value (external state change)
  useEffect(() => {
    setText(isoToEU(value));
  }, [value]);

  const commit = (raw) => {
    if (!raw) {
      onChange?.("");
      return;
    }
    const iso = euToIso(raw);
    if (iso) onChange?.(iso);
  };

  const handleBlur = () => commit(text);
  const handleChange = (e) => {
    const masked = maskInput(e.target.value);
    setText(masked);
    if (masked.length === 10) commit(masked);
  };
  const handleNative = (e) => {
    const iso = e.target.value;
    setText(isoToEU(iso));
    onChange?.(iso || "");
  };

  const openPicker = () => {
    if (!nativeRef.current) return;
    if (typeof nativeRef.current.showPicker === "function") {
      try { nativeRef.current.showPicker(); return; } catch { /* fallthrough */ }
    }
    nativeRef.current.click();
    nativeRef.current.focus();
  };

  const height = inputSize === "sm" ? "py-1.5" : "py-2";

  return (
    <div className="relative inline-block w-full">
      <input
        type="text"
        inputMode="numeric"
        value={text}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        maxLength={10}
        aria-label="Date (dd/mm/yyyy)"
        className={`w-full bg-transparent border-b border-black/15 dark:border-white/15 ${height} pr-8 outline-none focus:border-black dark:focus:border-white transition-colors text-sm tabular-nums disabled:opacity-50 ${className}`}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-black dark:hover:text-white disabled:opacity-30"
        aria-label="Open date picker"
        tabIndex={-1}
      >
        <CalendarIcon size={14} />
      </button>
      <input
        ref={nativeRef}
        type="date"
        value={value || ""}
        onChange={handleNative}
        min={min}
        max={max}
        disabled={disabled}
        className="sr-only pointer-events-none"
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
}

/** Progressive digit mask → "dd/mm/yyyy" as the user types. */
function maskInput(raw) {
  const digits = String(raw).replace(/\D/g, "").slice(0, 8);
  const parts = [];
  if (digits.length > 0) parts.push(digits.slice(0, 2));
  if (digits.length > 2) parts.push(digits.slice(2, 4));
  if (digits.length > 4) parts.push(digits.slice(4, 8));
  return parts.join("/");
}
